<script setup lang="ts">
import { onMounted, ref, watchEffect, computed, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { IonButton, IonContent, IonIcon } from '@ionic/vue';
import { chevronBackOutline, closeOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import { TransactionType } from '@/enums/wallet.enum';
import { useCart } from '@/modules/shop/composables/useCart';
import { useCheckout } from '@/shared/composables/useCheckout';
import EventsItemMemberAdder from '@/modules/events/components/EventsItemMemberAdder.vue';
import ModalOne from '@/shared/components/ui/ModalOne.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useEventsTicketsAddMembers from '@/modules/events/composables/useEventsTicketsAddMembers';
import useUserProfile from '@/modules/profile/composables/useUserProfile';

// Init
const { t } = useI18n();
const route = useRoute();
const eventId = +route.params.id;
const isModalOpen = ref(false);
const { calculateTotalsPerProductCurrency, getCartItems } = useCart();
const { processCheckout } = useCheckout();
const { updateTicketData } = useEventsTicketsAddMembers();
const { getUserProfile } = useUserProfile();
const { isFetchedUserProfile, userProfile } = getUserProfile();
const isCheckoutDisabled = ref(true);

// OnMounted Hook
onMounted(async () => {
	await updateTicketData();
	await nextTick();
	checkIfCheckoutIsAllowed();
});

// Function to show/hide the modal
const showHideModal = (openState: boolean) => {
	isModalOpen.value = openState;
};

// Computed to check if all tickets are assigned
const checkIfCheckoutIsAllowed = () => {
	isCheckoutDisabled.value = !!getCartItems().some((cartItem) => !cartItem.additionalInfo.name);
};

// Watch cart items and enable checkout when valid
watchEffect(() => {
	checkIfCheckoutIsAllowed();
});

// Checkout handler
const canStartCheckout = () => {
	if (isCheckoutDisabled.value) {
		showHideModal(true);
	} else {
		processCheckout(TransactionType.PAYMENT, eventId);
	}
};
</script>

<template>
	<ion-content>
		<ModalOne :title="`${t('ticket.add_members.please_complete_title')}`" :is-open="isModalOpen">
			<template #content>
				<div class="font-sourcesanspro-regular text-sm text-center text-white/50 leading-[18px]">
					{{ t('ticket.add_members.please_complete_description') }}
				</div>
			</template>

			<template #actions>
				<div class="flex items-center justify-center">
					<div class="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-white">
						<ion-icon class="text-[38px] text-black cursor-pointer" :icon="closeOutline" @click="showHideModal(false)"></ion-icon>
					</div>
				</div>
			</template>
		</ModalOne>

		<div class="flex flex-col h-full justify-between px-[34px]">
			<div>
				<TopBar>
					<template #left>
						<ion-button class="w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full" shape="round">
							<router-link :to="`/events/${eventId}/tickets/buy`" class="flex items-center justify-center">
								<ion-icon :icon="chevronBackOutline" class="ion-no-padding ion-no-margin text-black mr-[2px]"></ion-icon>
							</router-link>
						</ion-button>
					</template>

					<template #center>
						<div class="flex grow items-center justify-center w-full">
							<div class="mr-2">
								<span class="font-sourcesanspro-semibold text-l2">
									{{ t('ticket.add_members.ticket_details') }}
								</span>
							</div>
						</div>
					</template>
				</TopBar>

				<div class="flex flex-col items-center justify-center mb-8">
					<h1 class="font-sourcesanspro-semibold text-lg2">{{ t('ticket.add_members.checkout') }}</h1>
					<span class="font-sourcesanspro-regular text-base2 text-white/50"> {{ getCartItems().length }} {{ t('ticket.add_members.tickets') }} </span>
				</div>

				<div v-if="isFetchedUserProfile" class="flex flex-col w-[100%] items-start">
					<EventsItemMemberAdder v-for="(cartItem, idx) in getCartItems()" :key="idx" :cartItemIndex="idx" :cartItemProductId="cartItem.product.id" :cart-item="cartItem" :eventId="eventId" class="w-full" />
				</div>
			</div>

			<div class="mb-8" @click.prevent="canStartCheckout">
				<ion-button class="w-full hover:rounded-full hover:bg-white" :disabled="isCheckoutDisabled" expand="full" size="large" shape="round">
					<div class="flex w-full items-center justify-between">
						<span class="text-m2">{{ t('events.checkout') }}</span>
						<span>{{ calculateTotalsPerProductCurrency() }}</span>
					</div>
				</ion-button>
			</div>
		</div>
	</ion-content>
</template>
